import styled, { css } from 'styled-components';
import { media } from '@simplywallst/ui-core';

export const TooltipName = styled('span')`
  border-bottom: 1px dotted ${(props) => props.theme.color.brand01};
  cursor: help;
  width: fit-content;
`;

export const TipContainer = styled('p')`
  margin: 0;
  max-width: 200px;
  ${({ theme }) => theme.typography.tiny};
`;

export const MultipleVsIndustryDataTable = styled('table')`
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 4px;
  border: none;
  color: ${({ theme }) => theme.color.whiteRGBA(0.7)};
  text-align: left;
  padding: 0 ${({ theme }) => theme.x1spacePx}px;

  ${media.smDown`
    padding: 0;
    width: 100vw;
  `}

  thead tr {
    height: ${({ theme }) => theme.x5spacePx}px;
    position: sticky;
    inset-block-start: 0;
    z-index: 200;
    top: 4px;
  }

  thead tr th {
    ${({ theme }) => theme.typography.tinyStrong};
  }

  tbody tr {
    background: ${({ theme }) => theme.color.whiteRGBA(0.02)};
    ${({ theme }) => theme.typography.xSmall};
    height: ${({ theme }) => theme.x4spacePx - 4}px;

    ${media.smDown`
      height: ${({ theme }) => theme.x6spacePx}px;
    `}

    td:first-child {
      ${media.mdUp`
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      `}
    }
  }
  th,
  td {
    width: 20%;

    &:first-child {
      padding: 0px 12px;
      width: 40%;
      ${media.mdUp`
        width: 30%;
      `}
    }

    &:nth-child(2) {
      width: 16%;
      ${media.mdUp`
        width: 20%;
      `}
    }

    &:last-child {
      width: 26%;
      ${media.mdUp`
        width: 20%;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      `}
    }
  }
`;

interface RowProps {
  sticky?: boolean;
}

export const IndustryAvgRow = styled('tr')<RowProps>`
  &&& {
    background: ${({ theme }) => theme.color.chart04RGBA(0.1)};
  }
  position: ${({ sticky }) => (sticky ? 'sticky' : 'static')};
  inset-block-start: 0;
  z-index: 200;
  top: 48px;
  border-top: 4px solid ${({ theme }) => theme.color.charcoal};
`;

export const IndustryAvgTitle = styled('td')`
  color: ${({ theme }) => theme.color.chart04};
`;

interface EstimatedGrowthTDProps {
  isNegative?: boolean;
}

export const EstimatedGrowthTD = styled('td')<EstimatedGrowthTDProps>`
  color: ${({ isNegative, theme }) => isNegative && theme.color.bad};
`;

export const SelectedRow = styled('tr')<RowProps>`
  position: ${({ sticky }) => (sticky ? 'sticky' : 'static')};
  inset-block-start: 0;
  z-index: 200;
  top: 80px;
  ${media.smDown`
    top: 100px;
  `}

  &&& {
    background: ${({ theme }) => theme.color.brand01RGBA(0.1)};
  }
`;

interface CompanyRowProps {
  disabled?: boolean;
}

export const CompanyRow = styled('tr')<CompanyRowProps>`
  ${({ disabled }) => !disabled && 'cursor: pointer;'}

  &:hover {
    ${({ theme, disabled }) =>
      !disabled && `background: ${theme.color.whiteRGBA(0.05)};`}
  }
`;

interface CompanyNameCellProps {
  selected?: boolean;
}

export const CompanyNameCell = styled('div')<CompanyNameCellProps>`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.color.whiteRGBA(0.7)};
  height: ${({ theme }) => theme.x4spacePx - 4}px;

  ${media.smDown`
    height: ${({ theme }) => theme.x6spacePx}px;
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;
  `}

  ${({ selected }) =>
    selected &&
    css`
      &::after {
        content: ' ';
        display: block;
        position: absolute;
        width: 4px;
        background-color: ${({ theme }) => theme.color.brand01};
        left: -12px;
        top: 0;
        bottom: 0;

        ${media.mdUp`
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        `}
      }
    `}
`;

export const TableSymbol = styled('a')`
  width: 100%;
  color: ${({ theme }) => theme.color.brand01};
  margin-right: 0.25em;
  text-decoration: none;

  ${media.mdUp`
    width: auto;
    white-space: nowrap;
  `}
`;

export const TableSpan = styled('span')`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
